import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { cn } from './cn';

/** Ant Design `InputNumber` replacement — controlled numeric input with min/max/step,
 *  optional formatter/parser (e.g. currency) and addon/prefix slots. onChange emits number|null. */
export function InputNumber({
  value, onChange, min, max, step = 1, precision, formatter, parser, prefix, addonAfter, addonBefore,
  disabled, readOnly, placeholder, size = 'middle', className, style, id, controls = true, stringMode, status, invalid,
  'aria-label': ariaLabel,
}: {
  value?: number | null;
  onChange?: (v: number | null) => void;
  min?: number; max?: number; step?: number; precision?: number;
  formatter?: (v: number | string | undefined) => string;
  parser?: (s: string) => string | number;
  prefix?: ReactNode; addonAfter?: ReactNode; addonBefore?: ReactNode;
  disabled?: boolean; readOnly?: boolean; placeholder?: string;
  size?: 'small' | 'middle' | 'large';
  className?: string; style?: CSSProperties; id?: string; controls?: boolean; stringMode?: boolean;
  status?: 'error' | 'warning'; invalid?: boolean;
  /** Names the control when it is not wrapped in a labelled Form.Item / Field (filter bars, toolbars). */
  'aria-label'?: string;
}) {
  const isErr = invalid || status === 'error';
  const [text, setText] = useState('');
  const [focused, setFocused] = useState(false);
  const ref = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (focused) return;
    setText(value == null || Number.isNaN(value) ? '' : String(value));
  }, [value, focused]);

  const clamp = (n: number) => {
    let x = n;
    if (min != null) x = Math.max(min, x);
    if (max != null) x = Math.min(max, x);
    if (precision != null) x = Number(x.toFixed(precision));
    return x;
  };
  /**
   * ONLY NUMBERS, and only in the shape a number actually takes.
   *
   * Dropping the characters that are never part of a number is not enough: it keeps every digit, dot
   * and hyphen wherever they happen to land, so pasting a slip reference like "E2E-SLIP-0001" left
   * "2--0001" sitting in an amount box. `Number()` reads that as NaN, so the field emitted null while
   * still DISPLAYING the text — a control showing one thing and holding another, on a form about money.
   *
   * So the string is rebuilt rather than filtered: at most one leading minus, at most one decimal
   * point, digits otherwise. A lone "-" or "." survives as text because it is a number half-typed;
   * `parse` treats both as empty until there is a digit to go with them.
   */
  const sanitize = (raw: string): string => {
    let s = raw.replace(/[^\d.-]/g, '');
    // A minus only means anything at the FRONT, and only where negatives are actually allowed. On a
    // field that cannot go below zero it is never a keystroke worth honouring.
    const negative = s.startsWith('-') && (min == null || min < 0);
    s = s.replace(/-/g, '');
    const dot = s.indexOf('.');
    if (dot !== -1) {
      // Everything after the first point keeps its digits; later points are dropped, so "1.2.3" while
      // typing becomes "1.23" instead of a value that can never parse.
      s = s.slice(0, dot + 1) + s.slice(dot + 1).replace(/\./g, '');
      // A whole-number field has no fractional part to type into.
      if (precision === 0) s = s.slice(0, dot);
      else if (precision != null) s = s.slice(0, dot + 1 + precision);
    }
    return (negative ? '-' : '') + s;
  };
  const parse = (raw: string): number | null => {
    const cleaned = parser ? parser(raw) : sanitize(raw);
    if (cleaned === '' || cleaned === '-' || cleaned === '.' || cleaned === '-.') return null;
    const n = Number(cleaned);
    return Number.isNaN(n) ? null : n;
  };
  const emit = (raw: string) => {
    // A custom parser owns its own sanitising (e.g. currency), so leave its input untouched.
    const cleaned = parser ? raw : sanitize(raw);
    setText(cleaned);
    const n = parse(cleaned);
    onChange?.(n == null ? null : clamp(n));
  };
  const bump = (dir: 1 | -1) => {
    const cur = value ?? 0;
    onChange?.(clamp(cur + dir * step));
  };

  const h = size === 'small' ? 'h-8 text-sm' : size === 'large' ? 'h-11 text-[15px]' : 'h-9 text-sm';
  const display = !focused && formatter ? formatter(value ?? undefined) : text;

  /*
   * `style` sizes the CONTROL, not the text inside it — antd's contract, and what every caller here
   * assumes when it writes `style={{ width: 80 }}`.
   *
   * It used to land on the inner <input> while the wrapper kept `w-full`, so a width did nothing to the
   * control and the field simply filled its flex row: in the returns "Receive goods" dialog the 80px
   * Qty box ate the whole line and squeezed the "Batch received" field to zero width — the batch
   * control the warehouse is asked to confirm was not rendered at all. 49 call sites pass a width this
   * way. Width/flex properties move to the wrapper; anything else (text-align, colour…) still reaches
   * the input, where it belongs.
   */
  const BOX_KEYS = new Set(['width', 'minWidth', 'maxWidth', 'flex', 'flexGrow', 'flexShrink', 'flexBasis']);
  const boxStyle: CSSProperties = {};
  const textStyle: CSSProperties = {};
  for (const [k, v] of Object.entries(style ?? {})) {
    (BOX_KEYS.has(k) ? boxStyle : textStyle)[k as keyof CSSProperties] = v as never;
  }
  /*
   * A width can arrive as a CLASS as well as a style, and `cn` is a plain joiner with no
   * tailwind-merge — so the default `w-full` below was emitted alongside a caller's `w-16` and won on
   * stylesheet order. The returns filter asked for two 64px quantity boxes and got two 165px ones,
   * which is what pushed that page 50px off a phone screen. Treat a width utility in `className` the
   * same as `style.width`: the caller has stated a width, so don't also assert one.
   */
  const hasWidth = boxStyle.width != null || /(?:^|\s)(?:w-|min-w-|max-w-|size-|basis-|flex-(?:1|auto|none|initial)\b)/.test(className ?? '');

  const field = (
    <div
      style={boxStyle}
      className={cn('relative flex items-stretch overflow-hidden rounded-control border bg-surface transition-colors focus-within:ring-2 focus-within:ring-brand-500/20', isErr ? 'border-danger-500' : 'border-line focus-within:border-brand-400', disabled && 'cursor-not-allowed opacity-60', (addonBefore || addonAfter || hasWidth) ? '' : 'w-full', className)}>
      {prefix != null && <span className="flex items-center pl-3 text-muted">{prefix}</span>}
      <input
        ref={ref}
        id={id}
        aria-label={ariaLabel}
        type="text"
        inputMode="decimal"
        role="spinbutton"
        aria-valuenow={value ?? undefined}
        aria-valuemin={min}
        aria-valuemax={max}
        disabled={disabled}
        readOnly={readOnly}
        placeholder={placeholder}
        value={display}
        onFocus={() => setFocused(true)}
        onBlur={() => { setFocused(false); if (text !== '') { const n = parse(text); onChange?.(n == null ? null : clamp(n)); } }}
        onChange={(e) => emit(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'ArrowUp') { e.preventDefault(); bump(1); } else if (e.key === 'ArrowDown') { e.preventDefault(); bump(-1); } }}
        className={cn('w-full min-w-0 bg-transparent px-3 text-content outline-none placeholder:text-subtle', h, prefix != null && 'pl-1.5', controls && 'pr-7')}
        style={textStyle}
      />
      {controls && !disabled && !readOnly && (
        <div className="absolute right-0 top-0 flex h-full w-6 flex-col border-l border-line">
          <button type="button" tabIndex={-1} aria-label="Increase" onClick={() => bump(1)} className="flex flex-1 items-center justify-center text-subtle hover:bg-surface-2 hover:text-content">
            <svg viewBox="0 0 12 12" className="size-2.5" fill="currentColor"><path d="M6 3l4 5H2z" /></svg>
          </button>
          <button type="button" tabIndex={-1} aria-label="Decrease" onClick={() => bump(-1)} className="flex flex-1 items-center justify-center border-t border-line text-subtle hover:bg-surface-2 hover:text-content">
            <svg viewBox="0 0 12 12" className="size-2.5" fill="currentColor"><path d="M6 9L2 4h8z" /></svg>
          </button>
        </div>
      )}
    </div>
  );

  if (!addonBefore && !addonAfter) return field;
  return (
    // Addon label(s) fused to the field: the addon keeps the outer rounded corner, the field flattens
    // ONLY the side that meets an addon (`rounded-l-none` / `rounded-r-none`) so they join seamlessly.
    <div className="flex items-stretch">
      {addonBefore != null && <span className="flex shrink-0 items-center whitespace-nowrap rounded-l-control border border-r-0 border-line bg-surface-2 px-3 text-sm text-muted">{addonBefore}</span>}
      <div className={cn('min-w-0 flex-1', addonBefore && '[&>div]:rounded-l-none', addonAfter && '[&>div]:rounded-r-none')}>{field}</div>
      {addonAfter != null && <span className="flex shrink-0 items-center whitespace-nowrap rounded-r-control border border-l-0 border-line bg-surface-2 px-3 text-sm text-muted">{addonAfter}</span>}
    </div>
  );
}
